import { memo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AutocompleteRenderOptionState,
  Box,
  Checkbox,
  styled,
  Typography,
} from "@mui/material";

import { LOCALE_MAP_TRANSLATIONS } from "../../../Locale";
import { CustomAutocomplete } from "../../../components/Inputs/CustomAutocomplete";
import { LanguagesType } from "../../interfaces";
import { TooltipLabel } from "../../../components/Tooltips";
import { initialState, REQUIRED_LANGAGE_ID } from "../../BannerEditor";

type OptionType = {
  id: string;
  value: string;
};

type OptionStyledProps = {
  isRequired?: boolean;
};

export const languagesOption: OptionType[] = Object.entries(
  LOCALE_MAP_TRANSLATIONS
).map(([key, value]) => ({
  id: key,
  value: value,
}));

const isRequiredLanguage = (option: OptionType) =>
  option.id === REQUIRED_LANGAGE_ID;

const OptionStyled = styled("li", {
  shouldForwardProp: (prop) => prop !== "isRequired",
})<OptionStyledProps>(({ theme, isRequired }) => ({
  cursor: isRequired ? "not-allowed" : "pointer",
  opacity: isRequired ? 0.6 : 1,

  "&.Mui-focused": {
    backgroundColor: isRequired
      ? "transparent"
      : theme.vars.palette.action.hover,
  },
}));

const renderOptions = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: OptionType,
  state: AutocompleteRenderOptionState
) => {
  const isRequired = isRequiredLanguage(option);

  return (
    <OptionStyled {...props} key={option.id} isRequired={isRequired}>
      <Checkbox
        sx={{ mr: 1, p: 0 }}
        checked={state.selected}
        disabled={isRequired}
      />
      <FormattedMessage id={option.value} />
    </OptionStyled>
  );
};

const getOptionLabel = (option: OptionType) => option.value;

const isOptionEqualToValue = (option: OptionType, value: OptionType) =>
  option.id === value.id;

const LanguageSelector = memo(
  ({
    error,
    onLanguageChange,
  }: {
    error: string | undefined;
    onLanguageChange: (languages: LanguagesType[]) => void;
  }) => {
    const intl = useIntl();
    const [selectorValue, setSelectorValue] = useState<OptionType[]>(
      initialState.languages
    );

    const handleChange = (_: React.SyntheticEvent, value: OptionType[]) => {
      const required = languagesOption.find(isRequiredLanguage);
      const next =
        required && !value.some(isRequiredLanguage)
          ? [required, ...value]
          : value;

      setSelectorValue(next);
      onLanguageChange(next);
    };

    return (
      <Box
        sx={{
          marginTop: 1,
          "& .MuiInputBase-root.MuiOutlinedInput-root": { borderRadius: "4px" },
        }}
      >
        <CustomAutocomplete
          disableCloseOnSelect={true}
          getOptionDisabled={isRequiredLanguage}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          label={
            <TooltipLabel
              label={intl.formatMessage({ id: "BANNER_LANGUAGE_SELECTOR" })}
              tooltipLabel={intl.formatMessage({
                id: "BANNER_LANGUAGE_SELECTOR_TOOLTIP",
              })}
              translateTooltip={false}
            />
          }
          limitTags={3}
          multiple
          onChange={handleChange}
          options={languagesOption}
          renderOptions={renderOptions}
          tagsLabelKey="value"
          translateTags={true}
          value={selectorValue}
        />
        {error && (
          <Typography
            sx={{
              color: "error.main",
              fontSize: "0.75rem",
              ml: 1.75,
            }}
          >
            <FormattedMessage id={error} />
          </Typography>
        )}
      </Box>
    );
  }
);

export default LanguageSelector;
